import { useState } from "react";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import useReveal from "@/hooks/useReveal";

export default function EmailCTA() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | error | success
  const ref = useReveal();

  const submit = (e) => {
    e.preventDefault();
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!ok) {
      setState("error");
      toast.error("Please enter a valid email address");
      return;
    }
    setState("success");
    toast.success("You're in!", { description: "Check your inbox for your 10% off code." });
  };

  return (
    <section
      ref={ref}
      data-testid="email-cta-section"
      className="reveal py-16 md:py-24"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="relative overflow-hidden rounded-3xl bg-[#0e0e10] text-white p-8 md:p-16">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#ff5722]/40 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#ff5722]/20 blur-3xl" aria-hidden="true" />

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="mono text-xs uppercase tracking-widest text-[#ff5722]">Members only</span>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] mt-3 tracking-tight">
                Get <span className="text-[#ff5722]">10% off</span><br />
                your first pair.
              </h2>
              <p className="mt-5 text-white/70 max-w-md">
                Early access to drops, restock alerts, and behind-the-scenes from the lab. That&apos;s it.
              </p>
            </div>

            <div className="lg:col-span-5">
              {state === "success" ? (
                <div data-testid="newsletter-success" className="p-6 rounded-2xl bg-white/10 border border-white/20 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#ff5722] flex items-center justify-center">
                    <Check size={22} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-xl">Welcome to STRYDE.</div>
                    <div className="text-sm text-white/70">Your code is on the way.</div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="newsletter-form" className="space-y-3">
                  <div className={`flex items-center gap-2 p-2 pl-5 rounded-full bg-white/10 border ${state === "error" ? "border-red-400" : "border-white/20 focus-within:border-white/60"} transition-colors`}>
                    <Mail size={18} className="text-white/60 shrink-0" />
                    <input
                      type="email"
                      inputMode="email"
                      required
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setState("idle"); }}
                      placeholder="you@somewhere.com"
                      data-testid="newsletter-email-input"
                      className="flex-1 bg-transparent outline-none text-white placeholder-white/40 py-3 min-w-0"
                      aria-label="Email address"
                    />
                    <button
                      type="submit"
                      data-testid="newsletter-submit"
                      className="btn-accent whitespace-nowrap"
                    >
                      Subscribe
                    </button>
                  </div>
                  <p className="text-xs text-white/50 pl-2">No spam. Unsubscribe anytime.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
